import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { productsAPI } from 'api/products'
import type { Product } from 'Types/entities'
import { isOfflineQueuedFulfilledAction } from 'helpers/offlineAction'
import { branchChanged } from './branchSlice'

interface ProductsState {
 data: Product[]
 loading: boolean
 error: string | null
 pagination: {
 total: number
 per_page: number
 current_page: number
 last_page: number
 } | null
}

const initialState: ProductsState = {
 data: [],
 loading: false,
 error: null,
 pagination: null,
}

export const fetchProducts = createAsyncThunk(
 'products/fetchProducts',
 async (params: any = {}, { rejectWithValue }) => {
 try {
 const response = await productsAPI.getAll(params)
 return response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.products.fetchFailed'))
 }
 }
)

export const createProduct = createAsyncThunk(
 'products/createProduct',
 async (productData: any, { rejectWithValue }) => {
 try {
 const response = await productsAPI.create(productData)
 return response.data ?? response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.errors || error.response?.data?.message || i18n.t('errors.products.createFailed'))
 }
 }
)

export const updateProduct = createAsyncThunk(
 'products/updateProduct',
 async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
 try {
 const response = await productsAPI.update(id, data)
 const productData = response.data?.data ?? response.data ?? response
 // التأكد من وجود id في البيانات
 if (productData && !productData.id) {
 productData.id = id
 }
 return productData
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.errors || error.response?.data?.message || i18n.t('errors.products.updateFailed'))
 }
 }
)

export const deleteProduct = createAsyncThunk(
 'products/deleteProduct',
 async (id: number, { rejectWithValue }) => {
 try {
 const response = await productsAPI.delete(id)
 if (response?.status && response.status !== 'success') {
 return rejectWithValue(response.message || i18n.t('errors.products.deleteFailed'))
 }
 return { id, message: response?.message }
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.products.deleteFailed'))
 }
 }
)

const productsSlice = createSlice({
 name: 'products',
 initialState,
 reducers: {
 clearProducts: (state) => {
 state.data = []
 state.error = null
 state.pagination = null
 },
 },
 extraReducers: (builder) => {
 builder
 // Fetch products
 .addCase(fetchProducts.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchProducts.fulfilled, (state, action) => {
 state.loading = false
 const payload = action.payload as any
 // استخراج البيانات - قد تكون في payload.data أو payload مباشرة
 const data = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : [])
 state.data = data.filter((item: any) => item != null)
 // استخراج pagination - يجب أن يكون في payload.pagination
 state.pagination = payload?.pagination || null
 })
 .addCase(fetchProducts.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Create product
 .addCase(createProduct.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(createProduct.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const created = action.payload as { id?: number; _offlineOptimistic?: boolean }
 if (created?._offlineOptimistic || created?.id == null) {
 return
 }
 state.data.unshift(created as any)
 })
 .addCase(createProduct.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Update product
 .addCase(updateProduct.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(updateProduct.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const payload = action.payload as any
 const productId = payload?.id
 if (productId) {
 const index = state.data.findIndex((p) => p.id === productId)
 if (index !== -1) {
 state.data[index] = { ...state.data[index], ...payload }
 }
 }
 })
 .addCase(updateProduct.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Delete product
 .addCase(deleteProduct.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(deleteProduct.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const payload = action.payload as any
 const deletedId = typeof payload === 'number' ? payload : payload?.id
 state.data = state.data.filter((p) => p.id !== deletedId)
 })
 .addCase(deleteProduct.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export const { clearProducts } = productsSlice.actions
export default productsSlice.reducer
